import AwareComponent from '@af-common/Common/_components/AwareComponent';
import * as Immutable from 'immutable';
import Konva from 'konva';
import * as React from 'react';
import { Stage as KonvaStage } from 'react-konva';
import { Provider, ReactReduxContext } from "react-redux";
import { DocumentUnitTypes } from '../../Document/_stores/DocumentStore';
import LayerStore from '../../Layer/_stores/LayerStore';
import * as ModuleResolver from '../../Module/_config/ModuleResolver';
import ModuleStore from '../../Module/_stores/ModuleStore';
import Circle from '../../Utils/HitDetection/Circle';
import IBoundingBox from '../../Utils/HitDetection/IBoundingBox';
import Point from '../../Utils/HitDetection/Point';
import Rect from '../../Utils/HitDetection/Rect';
import CanvasWrapper from '../../Webgl/CanvasWrapper';
import CanvasWrapperFactory from '../../Webgl/CanvasWrapperFactory';
import Fxaa from '../../Webgl/PostProcessing/Fxaa';
import ViewerShader from '../../Webgl/Shader/ViewerShader';
import * as Uniform from '../../Webgl/Uniform';
import Webgl from '../../Webgl/Webgl';
import { DOCUMENT_DATA_DPI } from '../../_config/Consts';
import Transformer from '../_components/Transformer';
import RulersContainer from '../_containers/RulersContainer';
import TransformerContainer from '../_containers/TransformerContainer';
import WorkCanvasContainer from '../_containers/WorkCanvasContainer';

export type Props = {
	designId: number,
	
	viewerWidth: number,
	viewerHeight: number,
	documentOffsetX?: number,
	documentOffsetY?: number,
	documentWidth?: number,
	documentHeight?: number,
	documentDPI?: number,
	documentQuality?: number,
	documentUnits?: DocumentUnitTypes
	attachedModule?: string,

	gridEnabled?: boolean,
	overlayEnabled?: boolean,
	rulersEnabled?: boolean,

	previewFabricEnabled?: boolean,
	previewFabricWidth?: number,
	previewFabricHeight?: number,

	repeatMode?: 'tile'|'panel',

	layers?: Immutable.OrderedMap<string, LayerStore>,
	modules?: Immutable.OrderedMap<string, ModuleStore>,
	selectedModuleId?: string,
	selectLayer?:Function,
	selectModule?:Function,
	setDPI?:Function,
	setDocumentOffsetX?:Function,
	setDocumentOffsetY?:Function,
	moduleUpdater?:Function,
	spaceDown?:boolean,
}

export type State = {
	isDragging: boolean
}

export default class Stage extends AwareComponent<Props, State> {
	private viewerRef: React.RefObject<HTMLDivElement>;
	private canvas: CanvasWrapper;
	private transformerRef: React.RefObject<Transformer>;

	state: State = {
		isDragging: false
	}

	constructor(props: Props, state: State) {
		super(props, state);
		
		this.viewerRef = React.createRef();
		this.transformerRef = React.createRef();

		if (!this.canvas) {
			this.canvas = CanvasWrapperFactory.create(this.props.viewerWidth, this.props.viewerHeight);
		}

		let width:number = this.props.documentWidth / (DOCUMENT_DATA_DPI / this.props.documentDPI);
		let height:number = this.props.documentHeight / (DOCUMENT_DATA_DPI / this.props.documentDPI);

		Webgl.getInstance("viewer", this.props.viewerWidth, this.props.viewerHeight, this.canvas);

		//Center our pattern
		this.props.setDocumentOffsetX(this.props.viewerWidth / 2 - width / 2);
		this.props.setDocumentOffsetY(this.props.viewerHeight / 2 - height / 2);
	}

	componentDidMount() {
		this.setCurrentCanvas();
		
		Webgl.getInstance("viewer", this.props.viewerWidth, this.props.viewerHeight, this.canvas);
	}

	componentWillUnmount(){
		Webgl.flush();
	}

	componentDidUpdate(prevProps:Props, prevState:State){
		this.setCurrentCanvas();

		if(this.propChanged(prevProps, 'viewerWidth', 'viewerHeight')){
			Webgl.getInstance("viewer", this.props.viewerWidth, this.props.viewerHeight, this.canvas);
		}

		if(this.stateChanged(prevState, 'isDragging')){
			if(this.state.isDragging) this.addWindowEvents();
			else this.removeWindowEvents();
		}
	}

	private setCurrentCanvas = () => {
		//Check if current canvas in DOM matches the one attached to our CanvasWrapper
		let currentCanvas = this.viewerRef.current.querySelector('canvas');
		if(currentCanvas && currentCanvas != this.canvas.content) {
			this.viewerRef.current.removeChild(this.viewerRef.current.querySelector('canvas'));
		}

		//Add canvas to DOM if we don't have it
		if(!this.viewerRef.current.hasChildNodes()) {
			this.viewerRef.current.appendChild(this.canvas.content as HTMLCanvasElement);

			//Force a re-render if we lost webglcontext
			(this.canvas.canvas as HTMLCanvasElement).addEventListener('webglcontextrestored', (e) => {
				this.forceUpdate();
			});
		}
	}

	private addWindowEvents = () => {
		//window.addEventListener();
	}

	private removeWindowEvents = () => {
		//window.removeEventListener();
	}

	private hasModule = () => {
		return this.props.modules && this.props.selectedModuleId;
	}

	private hasTransformer = () => {
		if (!this.hasModule() || !this.props.selectedModuleId) return false;
		let module = this.props.modules.get(this.props.selectedModuleId);
		if (!module) return false;
		let enabled = ModuleResolver.hasTransformer(module);
		if (ModuleResolver.hasSource(module) && !module.get('source') || this.props.selectedModuleId === this.props.attachedModule) enabled = false;
		return enabled;
	};

	render() {
		return <>
			<div ref={this.viewerRef} id="viewer" />
			{!this.props.previewFabricEnabled &&
				<ReactReduxContext.Consumer>
					{({ store }) => (
						<KonvaStage
							onMouseDown={this.onMouseDown.bind(this)}
							onMouseMove={this.onMouseMove.bind(this)}
							onMouseUp={this.onMouseUp.bind(this)}
							width={this.props.viewerWidth}
							height={this.props.viewerHeight}
							style={{
								width: this.props.viewerWidth,
								height: this.props.viewerHeight,
								position: 'fixed',
								top: '65px'
							}}
						>
							<Provider store={store}>
								<RulersContainer enabled={this.props.rulersEnabled} />
								<TransformerContainer enabled={this.hasTransformer()} ref={this.transformerRef} module={this.props.modules.get(this.props.selectedModuleId)} moduleId={this.props.selectedModuleId}/>
							</Provider>
						</KonvaStage>
					)}
				</ReactReduxContext.Consumer>
			}
			<WorkCanvasContainer
				layers={this.props.layers}
				draw={this.updatePattern.bind(this)}
				documentWidth={this.props.documentWidth}
				documentHeight={this.props.documentHeight}
			/>
		</>
	}

	private updatePattern() {
		Fxaa("layers");
		let viewer:Webgl;

		let u_viewerInputSize:Uniform.Uniform2f;
		let u_viewerSize:Uniform.Uniform2f;
		let u_grid:Uniform.Uniform1i;
		let u_overlay:Uniform.Uniform1i;
		let u_rulers:Uniform.Uniform1i;
		let u_previewFabric:Uniform.Uniform1i;
		let u_previewFabricSize:Uniform.Uniform2f;
		let u_repeatMode:Uniform.Uniform1i;
		let u_flipy:Uniform.Uniform1i;
		let u_units:Uniform.Uniform1i;

		viewer = Webgl.getInstance("viewer", this.canvas.width, this.canvas.height);

		u_viewerInputSize = new Uniform.Uniform2f(this.props.documentWidth / (DOCUMENT_DATA_DPI / this.props.documentDPI), this.props.documentHeight / (DOCUMENT_DATA_DPI / this.props.documentDPI));
		u_viewerSize = new Uniform.Uniform2f(this.canvas.width, this.canvas.height);
		
		u_grid = new Uniform.Uniform1i(this.props.gridEnabled ? 1:0);
		u_overlay = new Uniform.Uniform1i(this.props.overlayEnabled ? 1:0);
		u_rulers = new Uniform.Uniform1i(this.props.rulersEnabled ? 1:0);
		u_previewFabric = new Uniform.Uniform1i(this.props.previewFabricEnabled ? 1:0);
		u_previewFabricSize = new Uniform.Uniform2f(this.props.previewFabricWidth*DOCUMENT_DATA_DPI, this.props.previewFabricHeight*DOCUMENT_DATA_DPI);
		u_repeatMode = new Uniform.Uniform1i(this.props.repeatMode === 'tile' ? 0 : 1);
		u_flipy = new Uniform.Uniform1i(1);

		let units = 0;
		switch (this.props.documentUnits) {
			case DocumentUnitTypes.Pixels:
				units = 1;
			break;
			case DocumentUnitTypes.Inches:
				units = 2;
			break;
			case DocumentUnitTypes.Centimeters:
				units = 3;
			break;
		}
		u_units = new Uniform.Uniform1i(units);

		if(this.props.previewFabricEnabled) {
			u_viewerInputSize = new Uniform.Uniform2f(this.props.documentWidth, this.props.documentHeight);
		}

		/*let fbo = viewer.fbos[viewer.getNextFbo("layers")];
		let webgl = Webgl.getInstance("layers", fbo.width, fbo.height);

		let gl = webgl.gl;
		let data:any = new Uint8Array(fbo.width * fbo.height * 4);
		gl.readPixels(0, 0, fbo.width, fbo.height, gl.RGBA, gl.UNSIGNED_BYTE, data);
		gl.bindFramebuffer(gl.FRAMEBUFFER, null);

		//let out = 'P3\n# gl.ppm\n' + canvas.width + " " + canvas.height + '\n255\n';
		//for(var i=0; i<data.length; i++) out += (i-3)%4 ? data[i] + ' ':'';
		console.log(data); return;*/

		if(!viewer.programs['viewer']){
			viewer.createProgram("viewer", new ViewerShader());
		}

		viewer.useProgram("viewer");

		(viewer.currentProgram.shader as ViewerShader).u_viewerInputSize = u_viewerInputSize;
		(viewer.currentProgram.shader as ViewerShader).u_offset = new Uniform.Uniform2f(this.props.documentOffsetX, this.props.documentOffsetY);
		(viewer.currentProgram.shader as ViewerShader).u_viewerSize = u_viewerSize;
		(viewer.currentProgram.shader as ViewerShader).u_dpi = new Uniform.Uniform1f(this.props.documentDPI);
		(viewer.currentProgram.shader as ViewerShader).u_units = u_units;
		
		(viewer.currentProgram.shader as ViewerShader).u_grid = u_grid;
		(viewer.currentProgram.shader as ViewerShader).u_overlay = u_overlay;
		(viewer.currentProgram.shader as ViewerShader).u_rulers = u_rulers;

		(viewer.currentProgram.shader as ViewerShader).u_previewFabric = u_previewFabric;
		(viewer.currentProgram.shader as ViewerShader).u_previewFabricSize = u_previewFabricSize;
		(viewer.currentProgram.shader as ViewerShader).u_repeatMode = u_repeatMode;
		(viewer.currentProgram.shader as ViewerShader).u_flipy = u_flipy;
		
		viewer.render(viewer.getPreviousFbo("layers"), false);
	}

	private onMouseDown(e:Konva.KonvaEventObject<MouseEvent>): void {
		//Return here if we're hitting something else than the stage
		if (e.target._id !== e.currentTarget._id) return;

		let canvasWidth = this.props.documentWidth;
		let canvasHeight = this.props.documentHeight;

		let action = new Point((e.evt.pageX - this.props.documentOffsetX)/(this.props.documentDPI / DOCUMENT_DATA_DPI), (e.evt.pageY - 65 - this.props.documentOffsetY)/(this.props.documentDPI / DOCUMENT_DATA_DPI));

		let coords = new Point(
			action.x % canvasWidth < 0 ? action.x % canvasWidth + canvasWidth : action.x % canvasWidth,
			action.y % canvasHeight < 0 ? action.y % canvasHeight + canvasHeight : action.y % canvasHeight,
		);

		let layers = Webgl.getInstance("layers");
		layers.useFbo(layers.getPreviousFbo("layers") + "_coords");

		coords = new Point(
			coords.x / canvasWidth * layers.fbos[layers.currentFramebuffer].width,
			coords.y / canvasHeight * layers.fbos[layers.currentFramebuffer].height
		);

		var pixels = new Uint8Array(4);
		layers.gl.readPixels(coords.x, coords.y, 1, 1, layers.gl.RGBA, layers.gl.UNSIGNED_BYTE, pixels);

		// Confirming that the coords data is fine by checking if blue is empty and alpha is full.
		if(pixels[2] == 0 && pixels[3] == 255) {
			coords = new Point(
				pixels[0] / 255 * canvasWidth,
				pixels[1] / 255 * canvasHeight
			)
		} else {
			this.props.selectModule(null);
		}

		// Execute drag action
		if(this.props.spaceDown && !this.props.overlayEnabled){
			this.setState({isDragging: true});
			return;
		}

		// Proceed with hit detection if we're not panning
		let hitFound = false;
		this.props.layers.reverse().valueSeq().some((layer: LayerStore, layerKey: number): boolean => {
			//Skip if layer isn't visible
			if (!layer.get('visible')) return false;

			layer.modules.reverse().valueSeq().some((moduleId: string, moduleKey: number): boolean => {
				//This kind of required since shapes end up outside the local coordinates. Not sure if there is a better way....
				hitFound = this.hitCheck(moduleId, coords, new Point(0, 0), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(-canvasWidth, 0), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(-canvasWidth, -canvasHeight), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(0, -canvasHeight), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(canvasWidth, 0), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(canvasWidth, canvasHeight), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(0, canvasHeight), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(-canvasWidth, canvasHeight), action);
				hitFound = hitFound || this.hitCheck(moduleId, coords, new Point(canvasWidth, -canvasHeight), action);

				if (hitFound && moduleId !== this.props.selectedModuleId) {
					this.props.selectLayer(layer.get('id'));
					this.props.selectModule(moduleId);
				}

				return hitFound;
			});
			return hitFound
		});

		//Deselect module if no hit found
		if (!hitFound) {
			this.props.selectModule(null);
		} else {
			this.transformerRef.current.ref?.fire('mousedown', e);
		}
	}

	private onMouseMove(e:Konva.KonvaEventObject<MouseEvent>): void {
		if(!this.state.isDragging) return;

		let animFrame = (func:FrameRequestCallback) => window.requestAnimationFrame(func);

		animFrame(() => {
			this.props.setDocumentOffsetX(this.props.documentOffsetX + e.evt.movementX);
			this.props.setDocumentOffsetY(this.props.documentOffsetY + e.evt.movementY);
		});
	}

	private onMouseUp(e:Konva.KonvaEventObject<MouseEvent>): void {
		this.setState({isDragging: false});
	}

	private hitCheck(moduleId: string, target: Point, offset: Point, mouseTarget: Point): boolean {
		let canvasWidth = this.props.documentWidth;
		let canvasHeight = this.props.documentHeight;
		let hitFound = false;

		let localBaseTarget = target;
		let localTarget = localBaseTarget.add(offset);

		let module = this.props.modules.get(moduleId);

		let boundingBox: IBoundingBox = null;

		let localModuleX: number = 0;
		let localModuleY: number = 0;

		//Skip modules with empty source
		if (ModuleResolver.hasSource(module) && !module.get('source')) {
			return false;
		}

		if (ModuleResolver.hasPosition(module)) {
			localModuleX = module.get('x') % canvasWidth;
			localModuleY = module.get('y') % canvasHeight;
		}

		if (ModuleResolver.hasDimensions(module) && ModuleResolver.hasRotation(module)) {
			boundingBox = new Rect(
				localModuleX - module.get('width') / 2,
				localModuleX + module.get('width') / 2,
				localModuleY - module.get('height') / 2,
				localModuleY + module.get('height') / 2,
				module.get('rotation')
			);
		}

		if (ModuleResolver.hasRadius(module)) {
			boundingBox = new Circle(
				localModuleX,
				localModuleY,
				module.get('radius')
			);
		}

		if(boundingBox && boundingBox.hit(localTarget)) {
			let localOffsetX, localOffsetY;

			//Get base offset of mouse
			localOffsetX = (mouseTarget.x >= 0 ? Math.floor(mouseTarget.x / canvasWidth) : Math.floor(mouseTarget.x / canvasWidth))*canvasWidth;
			localOffsetY = (mouseTarget.y >= 0 ? Math.floor(mouseTarget.y / canvasHeight) : Math.floor(mouseTarget.y / canvasHeight))*canvasHeight;

			//Add this target's offset in case it's different
			localOffsetX = Math.floor((localOffsetX - offset.x) / canvasWidth) * canvasWidth;
			localOffsetY = Math.floor((localOffsetY - offset.y) / canvasHeight) * canvasHeight;

			this.props.moduleUpdater(moduleId, {
				x: localOffsetX + localModuleX,
				y: localOffsetY + localModuleY
			});

			hitFound = true;
		}

		return hitFound;
	}
}